import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@ricoh/ria.client.core/hooks/component/popup';

import type { PermissionState } from '~/plugins/store/slices/feature/auth/permission';

import { useStoreDispatch } from '~/plugins/store/hooks/dispatch';
import { authSliceActions } from '~/plugins/store/slices/feature/auth';

import type { SWROptions, SWROptionsWithAccessLevel } from './_permission.type';

/**
 * Use Access Level.
 *
 * @param {keyof PermissionState['permission']} permissions Permissions.
 *
 * @returns {SWROptionsWithAccessLevel} Access Level hook.
 */
export function useAccessLevel(permissions: (keyof PermissionState['permission'])[]): SWROptionsWithAccessLevel {
  const { t } = useTranslation();
  const dispatch = useStoreDispatch();

  const toast = useToast();

  return useCallback<Func<[options?: SWROptions], SWROptions>>(
    ({ toastError = true, onError, ...options } = {}) => ({
      ...options,
      toastError: false,

      onError: (...args: Parameters<NonNullable<typeof onError>>) => {
        const [error] = args;
        if (error.status === 403) {
          dispatch(
            authSliceActions.permission_setState({
              keys: permissions,
              value: false,
            }),
          );
          return;
        }

        if (toastError) {
          toast.push({
            type: 'error',
            ...error.errorMessage(t),
          });
        }

        if (onError) {
          (onError as Func<Parameters<NonNullable<typeof onError>>>)(...args);
        }
      },
    }),
    [dispatch, permissions, t, toast],
  ) as SWROptionsWithAccessLevel;
}
